import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Seo from "../components/Seo";
import { apiGet, formatEuro } from "../lib/api";
import { buildBreadcrumbJsonLd, SHOP_DESCRIPTION, SHOP_TITLE } from "../lib/seo";
import "./Shop.css";

const CATEGORY_LABEL_BY_KEY = {
  "olio-evo": "Olio EVO",
  conserve: "Conserve",
  "forno-salato": "Forno salato",
  "forno-dolce": "Forno dolce",
  dolciumi: "Dolciumi",
  altri: "Altri prodotti"
};

const CATEGORY_ORDER = ["olio-evo", "conserve", "forno-salato", "forno-dolce", "dolciumi", "altri"];

function getCartQty(cartItems, productId) {
  const found = cartItems.find((item) => item.id === productId);
  return found ? Number(found.qty) || 0 : 0;
}

function getProductCategoryKey(product) {
  const imageUrl = typeof product?.image_url === "string" ? product.image_url : "";
  const match = imageUrl.match(/\/products\/([^/?#]+)\//i);
  if (!match) {
    return "altri";
  }

  const key = decodeURIComponent(match[1]).trim().toLowerCase();
  return CATEGORY_LABEL_BY_KEY[key] ? key : "altri";
}

function groupProductsByCategory(products) {
  const grouped = new Map();

  products.forEach((product) => {
    const categoryKey = getProductCategoryKey(product);
    if (!grouped.has(categoryKey)) {
      grouped.set(categoryKey, []);
    }
    grouped.get(categoryKey).push(product);
  });

  const sortedKeys = [
    ...CATEGORY_ORDER.filter((key) => grouped.has(key)),
    ...Array.from(grouped.keys()).filter((key) => !CATEGORY_ORDER.includes(key))
  ];

  return sortedKeys.map((key) => ({
    key,
    label: CATEGORY_LABEL_BY_KEY[key] || key,
    products: grouped.get(key) || []
  }));
}

const ShopPage = ({ onAddToCart, cartItems = [], onUpdateQty }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const groupedProducts = groupProductsByCategory(products);

  const renderProductCard = (product) => (
    <article key={product.id} className="shop-card">
      <Link className="shop-card-link" to={`/shop/${encodeURIComponent(product.slug)}`}>
        {product.image_url ? (
          <img className="shop-card-image" src={product.image_url} alt={product.name} />
        ) : (
          <div className="shop-card-image shop-card-image-placeholder">No image</div>
        )}
        <h3>{product.name}</h3>
        <p>{product.description}</p>
        <div className="shop-card-meta">
          <span>{formatEuro(product.price_cents)}</span>
          <span>{product.weight_grams} g</span>
        </div>
      </Link>
      <div className="shop-card-actions">
        {(() => {
          const qty = getCartQty(cartItems, product.id);
          if (qty <= 0) {
            return (
              <button className="shop-add-button" onClick={() => onAddToCart?.(product, 1)}>
                Aggiungi al carrello
              </button>
            );
          }

          return (
            <div className="shop-qty-controls">
              <button
                className="shop-qty-button"
                onClick={() => onUpdateQty?.(product.id, Math.max(0, qty - 1))}
                aria-label={`Diminuisci quantita ${product.name}`}
              >
                -
              </button>
              <span className="shop-qty-value">{qty}</span>
              <button
                className="shop-qty-button"
                onClick={() => onUpdateQty?.(product.id, Math.min(99, qty + 1))}
                aria-label={`Aumenta quantita ${product.name}`}
              >
                +
              </button>
            </div>
          );
        })()}
      </div>
    </article>
  );

  useEffect(() => {
    let mounted = true;

    const loadProducts = async () => {
      try {
        const data = await apiGet("/api/products");
        if (mounted) {
          setProducts(data.products || []);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err.message : "Unable to load products");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadProducts();

    return () => {
      mounted = false;
    };
  }, []);

  return (
    <section className="shop-page">
      <Seo
        title={SHOP_TITLE}
        description={SHOP_DESCRIPTION}
        path="/shop"
        jsonLd={buildBreadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Shop", path: "/shop" }
        ])}
      />
      <h2 className="shop-title">Prodotti locali in spedizione</h2>
      <p className="shop-subtitle">Acquista olio, pasta e biscotti artigianali con consegna in tutta Italia.</p>

      {loading && <p className="shop-state">Caricamento prodotti...</p>}
      {!loading && error && <p className="shop-state shop-error">Errore: {error}</p>}
      {!loading && !error && products.length === 0 && <p className="shop-state">Nessun prodotto disponibile.</p>}

      {!loading && !error && products.length > 0 && (
        <div className="shop-sections">
          {groupedProducts.length > 1 && (
            <nav className="shop-sections-nav" aria-label="Navigazione sezioni shop">
              {groupedProducts.map((section) => (
                <a key={section.key} className="shop-section-link" href={`#shop-section-${section.key}`}>
                  {section.label}
                </a>
              ))}
            </nav>
          )}

          {groupedProducts.map((section) => (
            <section key={section.key} id={`shop-section-${section.key}`} className="shop-section">
              <h3 className="shop-section-title">{section.label}</h3>
              <div className="shop-grid">{section.products.map((product) => renderProductCard(product))}</div>
            </section>
          ))}
        </div>
      )}
    </section>
  );
};

export default ShopPage;
